import { ENTRY_KINDS } from "./qualifyLead";

/**
 * U4 REPLYFULL (Agent Brains 5.1 L3: "structured lead card only (no prose)").
 * The lines of the reply prompt's `[LEAD]` slot, rendered from the latest
 * research card and nothing else.
 *
 * The card is codes only (U1), so a line here can only ever be a closed-shape
 * code. Even so, every code is matched against its shape before it is
 * rendered, and the lines go inside the prompt's quarantine: stored data is
 * data, never instructions (CLAUDE.md section 6).
 *
 * What is NEVER rendered: the band, the score, any confidence number, any
 * follower bucket or VIP signal, the card hash, the version. VIP shows as one
 * service-level entry and no more: it changes service level and routing,
 * never wording a number (CLAUDE.md sections 0.5 and 3).
 */

/** The slice of U1's `ResearchCard` the reply reads, typed locally so U4 imports nothing of U1's. */
export interface LeadCardForReply {
  readonly version: number;
  readonly cardHash: string;
  readonly ttlExpiresAt: string;
  readonly signals: {
    readonly specificity: readonly string[];
    readonly entryKind: string | null;
  };
  readonly inferences: readonly { readonly code: string; readonly confidence: number }[];
  /** The card's enrichment outcome, read by the never-reveal check only (cardReveal.ts); never rendered. */
  readonly enrichmentOutcome: string;
  readonly vip: {
    readonly flag: boolean;
    readonly confidence: number | null;
    readonly signals: readonly string[];
    readonly computed: boolean;
  };
}

/** Trusted and constant, outside the quarantine. */
export const LEAD_HEADER =
  "[LEAD] what the shop knows about this person, as codes. It shapes what you say and is never mentioned.";

/** The only thing VIP shows in the prompt. Service level, never a number. */
export const LEAD_SERVICE_ENTRY = "service_level: owner_attention";

const INTEREST = /^interest\.([a-z0-9_]{1,40})$/;
const TONE = /^tone\.([a-z0-9_]{1,40})$/;
const SPECIFICITY = /^spec\.([a-z0-9_]{1,40})$/;

function distinctSuffixes(codes: readonly string[], shape: RegExp): string[] {
  const out: string[] = [];
  for (const code of codes) {
    const m = shape.exec(code);
    const tail = m?.[1];
    if (tail === undefined || tail === "unknown" || out.includes(tail)) continue;
    out.push(tail);
  }
  return out;
}

/**
 * The lines for one turn. A missing card is `card: none`, a card past its TTL
 * (or with an unreadable one) is `card: stale`: the consumer checks freshness
 * and never silently reuses a stale card (Agent Brains section 10).
 */
export function renderLeadLines(card: LeadCardForReply | null, now: Date): string[] {
  if (card === null) return ["card: none"];
  const expires = Date.parse(card.ttlExpiresAt);
  if (!Number.isFinite(expires) || expires <= now.getTime()) return ["card: stale"];

  const codes = card.inferences.map((i) => i.code);
  const lines: string[] = [];
  const interest = distinctSuffixes(codes, INTEREST);
  if (interest.length > 0) lines.push(`interest: ${interest.join(", ")}`);
  const tone = distinctSuffixes(codes, TONE);
  if (tone.length > 0) lines.push(`tone: ${tone.join(", ")}`);
  const specificity = distinctSuffixes(card.signals.specificity, SPECIFICITY);
  if (specificity.length > 0) lines.push(`specificity: ${specificity.join(", ")}`);
  const entry = card.signals.entryKind;
  if (entry !== null && (ENTRY_KINDS as readonly string[]).includes(entry)) lines.push(`entry: ${entry}`);
  if (card.vip.flag === true) lines.push(LEAD_SERVICE_ENTRY);
  return lines.length > 0 ? lines : ["card: empty"];
}
